import type { SeriesCommentAttributes, SeriesTagAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import {
  SUMMER_CAMP_FEATURED_SERIES_TAG,
  SUMMER_CAMP_SERIES_TAG,
} from '@/client/services/summerCamp/constants'
import { SeriesTag as SeriesTagType } from '@/client/services/tags/types'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import SeriesQ from './SeriesQ'

const SeriesTagQ = {
  ...makeQueryModelV2<SeriesTagAttributes>('series_tag')([
    'id',
    'user_id',
    'series_id',
    'tag',
    'updated_at',
    'created_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Series: {
      query: SeriesQ,
      foreignKey: SeriesQ.id,
      sourceKey: SeriesTagQ.series_id,
    },
  }),

  hasTag: (tag: SeriesTagType): SQLFragment<boolean> => SQL`${SeriesTagQ.tag} = ${tag}`,

  hasAnyTag: (tags: SeriesTagType[]): SQLFragment<boolean> => SQL`${SeriesTagQ.tag} = ANY(${tags})`,

  isSummerCampTag: (): SQLFragment<boolean> => SeriesTagQ.hasTag(SUMMER_CAMP_SERIES_TAG),

  getHighlyEngagingBootcampsForUser: (
    userId: number | SQLFragment<number>,
    customWhere?: SQLFragment<unknown>
  ): SQLFragment<boolean> =>
    SeriesTagQ.where(
      {
        tag: SeriesTagType.HighlyEngagingSATBootcamp,
        include: [
          {
            association: 'Series',
            required: true,
            where: SeriesQ.isHost({ userId }),
          },
        ],
      },
      customWhere
    ),

  isSummerCampFeaturedTag: (): SQLFragment<boolean> =>
    SeriesTagQ.hasTag(SUMMER_CAMP_FEATURED_SERIES_TAG),
  where: (
    {
      paranoid = true,
      userId,
      seriesId,
      tag,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      paranoid?: boolean
      userId?: number | null | SQLFragment<number>
      seriesId?: number | null | SQLFragment<number>
      tag?: SeriesTagType | null
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SeriesCommentAttributes> => SQL`
        FROM ${SeriesTagQ}
        ${includeAssociationsToSql(SeriesTagQ, include)}
        WHERE
            ${paranoid ? SQL`${SeriesTagQ.deleted_at} IS NULL AND` : SQL``}
            ${userId != null ? SQL`${SeriesTagQ.user_id} = ${userId} AND` : SQL``}
            ${seriesId != null ? SQL`${SeriesTagQ.series_id} = ${seriesId} AND` : SQL``}
            ${tag != null ? SQL`${SeriesTagQ.tag} = ${tag} AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,
}

export default SeriesTagQ
